/**
 * <CertificacaoCatalogo /> — CATÁLOGO das 6 Certificações da FEO.
 *
 * Catálogo enxuto (mesma função do /pos): mostra as informações gerais e
 * distribui o tráfego para a LP de cada certificação (hoje no Wix).
 * Copys e identidade visual espelham o catálogo atual do Wix.
 * Destaque de preço só em Ombro e Joelho (lote promocional R$400 OFF).
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, MessageCircle, Minus, Plus } from "lucide-react";

import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { CERT_LIST, CERT_WHATSAPP, type CertSlug } from "@/lib/certificacao/config";
import { CERT_DATA } from "@/lib/certificacao/data";
import { certOfertaAtiva, CERT_ORDER } from "@/lib/certificacao/oferta";
import { SemanaFisioTopBar, SemanaFisioOfferStrip } from "@/components/campanhas/semana-fisio-action";
import { BannerSlideView } from "@/components/home/BannerSlideView";
import { SLIDE_REGISTRY } from "@/components/home/banner-slides";
import depAsha from "@/assets/certificacao/dep-asha.png.asset.json";
import depCarolina from "@/assets/certificacao/dep-carolina.png.asset.json";
import depJessica from "@/assets/certificacao/dep-jessica.png.asset.json";

import hexBg from "@/assets/certificacao/hex-bg.jpg.asset.json";
import seloVariacao from "@/assets/certificacao/selo-variacao.png.asset.json";
import seloAutoridade from "@/assets/certificacao/selo-autoridade.png.asset.json";
import seloCertificacao from "@/assets/certificacao/selo-certificacao.png.asset.json";
import foguete from "@/assets/certificacao/foguete.png.asset.json";
import difMedalha from "@/assets/certificacao/dif-medalha.png.asset.json";
import difRelogio from "@/assets/certificacao/dif-relogio.png.asset.json";
import difChave from "@/assets/certificacao/dif-chave.png.asset.json";
import difRoteiro from "@/assets/certificacao/dif-roteiro.png.asset.json";

import catalogImg0 from "@/assets/certificacao/catalogo/33b784_0e2d3fcbb81c44289dc21fe76596b7fd.webp.asset.json";
import catalogImg1 from "@/assets/certificacao/catalogo/33b784_1f437a85c26643838c1ac85d11b07648.webp.asset.json";
import catalogImg2 from "@/assets/certificacao/catalogo/33b784_2ce706d5d56344cdad9e5ca91ce3bc77.webp.asset.json";
import catalogImg3 from "@/assets/certificacao/catalogo/33b784_2db2a87d09af4164bf459193ccb51f43.webp.asset.json";
import catalogImg4 from "@/assets/certificacao/catalogo/33b784_497ab25a512e4c9b9400386669c436a8.webp.asset.json";
import catalogImg5 from "@/assets/certificacao/catalogo/33b784_51de31255d6541f8b09291b0ccc950ec.webp.asset.json";
import catalogImg6 from "@/assets/certificacao/catalogo/33b784_6686c2fd2dc2476b895cb00f29d22478.webp.asset.json";
import catalogImg7 from "@/assets/certificacao/catalogo/33b784_6bbe04a8b2c54578802515ebf0cd5d99.webp.asset.json";
import catalogImg8 from "@/assets/certificacao/catalogo/33b784_78514877e3c04744b983258bf11f3b23.webp.asset.json";
import catalogImg9 from "@/assets/certificacao/catalogo/33b784_8650f0dc6c874cd9994f1ecf4c96564e.webp.asset.json";
import catalogImg10 from "@/assets/certificacao/catalogo/33b784_eac65375905a49bfa089b6b473d53a66.webp.asset.json";
const CATALOG_IMAGES: Record<string, string> = {
  "33b784_0e2d3fcbb81c44289dc21fe76596b7fd": catalogImg0.url,
  "33b784_1f437a85c26643838c1ac85d11b07648": catalogImg1.url,
  "33b784_2ce706d5d56344cdad9e5ca91ce3bc77": catalogImg2.url,
  "33b784_2db2a87d09af4164bf459193ccb51f43": catalogImg3.url,
  "33b784_497ab25a512e4c9b9400386669c436a8": catalogImg4.url,
  "33b784_51de31255d6541f8b09291b0ccc950ec": catalogImg5.url,
  "33b784_6686c2fd2dc2476b895cb00f29d22478": catalogImg6.url,
  "33b784_6bbe04a8b2c54578802515ebf0cd5d99": catalogImg7.url,
  "33b784_78514877e3c04744b983258bf11f3b23": catalogImg8.url,
  "33b784_8650f0dc6c874cd9994f1ecf4c96564e": catalogImg9.url,
  "33b784_eac65375905a49bfa089b6b473d53a66": catalogImg10.url,
};

export const TITLE = "Certificações FEO";
export const DESC =
  "Certificações da Fisio em Ortopedia: coluna lombar, cervical, agentes eletrofísicos, ombro, tornozelo e pé e joelho. Cursos 100% online de 60h, com aulas práticas e certificado.";
export const URL = "https://fisioemortopedia.com.br/certificacao";
export const OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/uJh6Ec5dB7QIUilgPzSo9fT36WL2/social-images/social-1783178899843-ChatGPT_Image_4_de_jul._de_2026,_12_28_08.webp";

/** LPs oficiais com checkout embutido. */
export const LP_OFICIAL = {
  lombar: "/certificacao-lombar",
  cervical: "/certificacao-cervical",
  eletro: "/certificacao-eletro",
  ombro: "/certificacao-ombro",
  tornozelo: "/certificacao-tornozelo",
  joelho: "/certificacao-joelho",
} as const;

/** Tons de azul do KV das certificações. */
const NAVY = "#05061c";
const NAVY_DEEP = "#02030f";
const BLUE = "#1F5FD1";
const BLUE_LIGHT = "#4FD9FF";

/** Textura oficial de hexágonos (KV enviado pelo designer). */
const HEX_LAYER = `url(${hexBg.url})`;

/** Fundo padrão das seções: textura de hexágonos + véu azul-marinho. */
const HEX_SECTION: React.CSSProperties = {
  backgroundImage: `linear-gradient(180deg, rgba(5,6,28,0.82) 0%, rgba(2,3,15,0.9) 100%), ${HEX_LAYER}`,
  backgroundSize: "cover, cover",
  backgroundPosition: "center, center",
};

/** Linha/realce do KV — degradê azul escuro → azul claro. */
const BLUE_LINE = `linear-gradient(90deg, transparent, ${BLUE}, ${BLUE_LIGHT}, ${BLUE}, transparent)`;



const SELO_VARIACAO = seloVariacao.url;
const SELO_AUTORIDADE = seloAutoridade.url;
const SELO_ALUNOS = seloCertificacao.url;
const FAQ_IMG = CATALOG_IMAGES["33b784_2ce706d5d56344cdad9e5ca91ce3bc77"];

const DIFERENCIAIS = [
  {
    icon: difMedalha.url,
    t: "Professores Renomados",
    d: "Aprenda com nossas referências dentro da fisioterapia no país",
  },
  {
    icon: difRelogio.url,
    t: "Certificações de 60h",
    d: "Cursos 100% online com vídeo aulas gravadas e materiais complementares",
  },
  {
    icon: difRoteiro.url,
    t: "Roteiros Práticos",
    d: "Desenhamos um passo a passo pensando na realidade do consultório",
  },
  {
    icon: difChave.url,
    t: "2 anos de acesso",
    d: "Matricule-se e tenha acesso imediato ao curso e suas atualizações",
  },
];

const PROF_IMG = (id: string) => CATALOG_IMAGES[id] ?? "";

const PROFESSORES = [
  { nome: "Rafael Alaiti", id: "33b784_78514877e3c04744b983258bf11f3b23" },
  { nome: "Leandro Fukusawa", id: "33b784_497ab25a512e4c9b9400386669c436a8" },
  { nome: "Josimari De Santana", id: "33b784_8650f0dc6c874cd9994f1ecf4c96564e" },
  { nome: "Murilo DeMarco", id: "33b784_51de31255d6541f8b09291b0ccc950ec" },
  { nome: "Isadora Oliveira", id: "33b784_1f437a85c26643838c1ac85d11b07648" },
  { nome: "Renato Soares", id: "33b784_0e2d3fcbb81c44289dc21fe76596b7fd" },
  { nome: "Julio Fernandes", id: "33b784_6686c2fd2dc2476b895cb00f29d22478" },
  { nome: "Carlos Pinfildi", id: "33b784_eac65375905a49bfa089b6b473d53a66" },
  { nome: "Yasmin El Hage", id: "33b784_6bbe04a8b2c54578802515ebf0cd5d99" },
  { nome: "Guilherme Paiva", id: "33b784_2db2a87d09af4164bf459193ccb51f43" },
];

/** Depoimentos das Certificações (conteúdo oficial do KV). */
const DEPOIMENTOS_CERT: { nome: string; foto: string; texto: React.ReactNode }[] = [
  {
    nome: "Asha Donini",
    foto: depAsha.url,
    texto: (
      <>
        É um ponto de virada na história da Fisioterapia musculoesquelética no Brasil. O portal está ressignificando o
        papel do fisioterapeuta em <strong className="font-semibold text-white">tempos modernos.</strong> Abriu portas
        para se o desenvolvimento profissional.
      </>
    ),
  },
  {
    nome: "Carolina Nakazuna",
    foto: depCarolina.url,
    texto: (
      <>
        Professores altamente qualificados a um valor super acessível. Recomendo para quem é estudante, graduado ou
        queira se aprofundar em temas relacionados ao sistema{" "}
        <strong className="font-semibold text-white">musculoesquelético.</strong>
      </>
    ),
  },
  {
    nome: "Jéssica Béda",
    foto: depJessica.url,
    texto: (
      <>
        Um marco na fisioterapia brasileira. Conteúdos de excelente qualidade, disponíveis de forma prática para{" "}
        <strong className="font-semibold text-white">qualificar nossa atuação</strong>.
      </>
    ),
  },
];

const FAQ = [
  {
    q: "Por quanto tempo terei acesso à certificação?",
    a: "O conteúdo completo da Certificação ficará disponível por dois anos contados a partir do momento da sua inscrição.",
  },
  {
    q: "As certificações são somente para fisioterapeutas?",
    a: "Sim, as certificações foram pensadas para fisioterapeutas que já atuam na área e trabalham com pacientes que possuem queixas musculoesqueléticas.",
  },
  {
    q: "Como posso tirar dúvidas com os professores?",
    a: "Você poderá deixar suas dúvidas em nossa plataforma de ensino. As suas perguntas serão respondidas por nossos professores!",
  },
  {
    q: "Como será a entrega do certificado?",
    a: "O certificado será entregue após você realizar todas às aulas da Certificação. Você poderá retirá-lo dentro da nossa plataforma de ensino.",
  },
  {
    q: "Terei acesso imediato à todos os módulos?",
    a: "Sim, todo o conteúdo ficará disponível na plataforma a partir do momento que você fizer sua inscrição.",
  },
  {
    q: "O conteúdo das Certificações é o mesmo que está nos cursos da Star?",
    a: 'Não, o conteúdo das Certificações é exclusivo e com uma visão aprofundada para transformar o fisioterapeuta em "expert" na temática. São 60h de conteúdos dedicados ao aprofundamento em cada certificação.',
  },
];

/** Ordem fixa do catálogo: lombar, cervical, eletro, ombro, tornozelo, joelho. */
const CARDS = CERT_ORDER.map((slug) => CERT_LIST.find((c) => c.slug === slug)).filter((c): c is (typeof CERT_LIST)[number] => c !== undefined);

export function CertificacaoCatalogo({ acao = false }: { acao?: boolean } = {}) {
  return (
    <div className="min-h-screen text-white" style={{ background: NAVY }}>
      {acao && <SemanaFisioTopBar off="72%" />}
      {/* HERO */}
      <section className="relative overflow-hidden py-14 sm:py-20" style={HEX_SECTION}>
        <div
          className="pointer-events-none absolute -top-56 left-1/2 h-[480px] w-[960px] max-w-none -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: `radial-gradient(closest-side, ${BLUE} 0%, rgba(31,95,209,0.35) 45%, transparent 75%)` }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
          style={{ background: `linear-gradient(180deg, transparent, ${NAVY})` }}
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <img decoding="async" src={SELO_VARIACAO} alt="Certificações Fisio em Ortopedia" className="mx-auto h-24 w-auto sm:h-28" />
          <h1 className="mt-7 text-balance text-3xl font-light leading-[1.12] tracking-tight sm:text-5xl">
            Aprimore suas <span className="font-semibold">Técnicas de Tratamento</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-pretty text-sm leading-relaxed text-white/70 sm:text-base">
            Cursos 100% online com o que há de mais novo na ciência. Certificações repletas de aplicações clínicas e
            aulas práticas.
          </p>
          <div className="mt-8 h-px w-full" style={{ background: BLUE_LINE, opacity: 0.9 }} />
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 sm:text-xs">
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">60h de conteúdo</span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">100% online</span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">COM CERTIFICADO</span>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section
        className="relative overflow-hidden py-12 sm:py-16"
        style={{ background: `linear-gradient(180deg, #0A1240 0%, ${NAVY} 100%)` }}
      >
        <h2 className="sr-only">Diferenciais das certificações FEO</h2>
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-x-6 gap-y-10 px-4 lg:grid-cols-4">
          {DIFERENCIAIS.map((d) => (
            <div key={d.t} className="text-center">
              <img
                src={d.icon}
                alt=""
                aria-hidden="true"
                loading="lazy" decoding="async"
                className="mx-auto h-24 w-24 object-contain sm:h-28 sm:w-28"
              />
              <h3 className="mt-4 text-sm font-bold sm:text-base">{d.t}</h3>
              <p className="mx-auto mt-2 max-w-[16rem] text-xs leading-relaxed text-white/60 sm:text-sm">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DESTAQUE: lançamento Certificação em Quadril (mesmo visual do slide da home) */}
      <section className="relative" aria-label="Lançamento: Certificação em Quadril">
        <BannerSlideView slide={SLIDE_REGISTRY.certquadrilvenda} className="relative" hideDescription compact />
      </section>


      {/* CATÁLOGO */}
      <section id="checkout" className="relative py-16 sm:py-24" style={{ background: NAVY }}>
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <img
              src={foguete.url}
              alt=""
              aria-hidden="true"
              loading="lazy" decoding="async"
              className="mx-auto mb-6 h-24 w-24 object-contain"
            />
            <h2 className="text-2xl font-light leading-snug sm:text-4xl">
              Escolha como você quer <span className="font-semibold">começar</span>
            </h2>
            <p className="mx-auto mt-4 text-pretty text-sm leading-relaxed text-white/65 sm:text-base">
              Cursos 100% baseados em evidências para você dominar as áreas mais desafiadoras da sua carreira na fisioterapia musculoesquelética
            </p>
          </div>

          {acao && (
            <div className="mx-auto mt-10 max-w-3xl">
              <SemanaFisioOfferStrip off="72%" />
            </div>
          )}

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CARDS.map((c) => {
              const o = certOfertaAtiva(c.slug, acao);
              const logo = CERT_DATA[c.slug]?.logo;
              return (
                <Link
                  key={c.slug}
                  to={LP_OFICIAL[c.slug]}
                  className="group relative flex flex-col overflow-hidden rounded-3xl border bg-white/[0.03] p-6 transition-all duration-300 hover:-translate-y-1"
                  style={{
                    borderColor: o.promo ? `${c.from}80` : "rgba(255,255,255,0.10)",
                    boxShadow: o.promo ? `0 24px 60px -34px ${c.from}` : undefined,
                  }}
                >
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
                    style={{ background: `linear-gradient(90deg,${c.from},${c.to})` }}
                  />
                  {o.promo && (
                    <span
                      className="absolute right-5 top-5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-black"
                      style={{ background: `linear-gradient(135deg,${c.from},${c.to})` }}
                    >
                      {o.descontoCurto}
                    </span>
                  )}

                  <div className="flex h-14 items-center">
                    {logo ? (
                      <img src={logo} alt={`Logo ${c.nome}`} loading="lazy" decoding="async" className="h-11 w-auto max-w-[80%]" />
                    ) : (
                      <span className="text-lg font-semibold">{c.nome}</span>
                    )}
                  </div>

                  <div className="mt-5">
                    <p className="text-[11px] uppercase tracking-wide text-white/45">
                      DE <s className="text-white/35">R$697</s> POR
                    </p>
                    <p className="mt-1 text-3xl font-extrabold" style={{ color: o.promo ? c.from : "#FFFFFF" }}>
                      {o.parcelado}
                    </p>
                    <p className="mt-1 text-sm text-white/70">{o.aVista}</p>
                  </div>

                  <span
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-bold tracking-wide text-black transition group-hover:brightness-110"
                    style={{ background: `linear-gradient(135deg,${c.from},${c.to})` }}
                  >
                    INICIAR AGORA
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROFESSORES */}
      <section
        className="py-16 sm:py-20"
        style={{ background: `linear-gradient(180deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)` }}
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <img src={SELO_AUTORIDADE} alt="" aria-hidden="true" loading="lazy" decoding="async" className="mx-auto h-28 w-28 object-contain sm:h-32 sm:w-32" />
            <h2 className="mt-5 text-2xl font-light leading-snug sm:text-4xl">
              Aprenda com quem <span className="font-semibold">domina o assunto</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-white/65 sm:text-base">
              Veja quem são os fisioterapeutas que te guiarão nesta jornada
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
            {[...PROFESSORES].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")).map((p) => (
              <figure key={p.nome} className="text-center">
                <img
                  src={PROF_IMG(p.id)}
                  alt={`${p.nome}, professor(a) das Certificações da Fisio em Ortopedia`}
                  loading="lazy" decoding="async"
                  className="mx-auto h-36 w-auto object-contain sm:h-44"
                />
                <figcaption className="mt-3 text-xs font-semibold sm:text-sm">{p.nome}</figcaption>
              </figure>
            ))}
          </div>

        </div>
      </section>

      {/* DESTAQUE (repetido): lançamento Certificação em Quadril */}
      <section className="relative" aria-label="Lançamento: Certificação em Quadril">
        <BannerSlideView slide={SLIDE_REGISTRY.certquadrilvenda} className="relative" hideDescription compact />
      </section>

      {/* DEPOIMENTOS */}
      <Depoimentos />

      {/* FAQ */}
      <Faq />

      <a
        href={CERT_WHATSAPP}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar com o suporte no WhatsApp"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center justify-center rounded-full bg-[#25D366] p-4 text-white shadow-lg shadow-[#25D366]/30 transition-transform hover:scale-110"
      >
        <MessageCircle className="h-6 w-6" aria-hidden="true" />
      </a>

      <MinimalFooter />
    </div>
  );
}

/** Prova social — depoimentos de alunos das Certificações (KV oficial). */
function Depoimentos() {
  return (
    <section className="py-16 sm:py-20" style={{ background: `linear-gradient(180deg, #141A8F 0%, #101569 55%, ${NAVY} 100%)` }}>
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <img src={SELO_ALUNOS} alt="" aria-hidden="true" loading="lazy" decoding="async" className="mx-auto h-28 w-28 object-contain sm:h-32 sm:w-32" />
          <h2 className="mt-5 text-2xl font-light leading-snug sm:text-4xl">
            +400 alunos já <span className="font-semibold">aprovaram</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
            Veja como ajudamos nossos alunos a alavancarem suas carreiras
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DEPOIMENTOS_CERT.map((d) => (
            <figure
              key={d.nome}
              className="flex h-full flex-col rounded-3xl border border-white/12 bg-black/35 p-6"
            >
              <div className="flex items-center gap-4">
                <img
                  src={d.foto}
                  alt={`${d.nome}, fisioterapeuta`}
                  loading="lazy" decoding="async"
                  className="h-14 w-14 shrink-0 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <figcaption className="text-base font-medium">{d.nome}</figcaption>
                  <p className="text-sm italic text-white/70">Fisioterapeuta</p>
                </div>
              </div>
              <blockquote className="mt-7 text-sm leading-relaxed text-white/85">{d.texto}</blockquote>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}


function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="relative py-16 sm:py-20" style={{ background: `linear-gradient(180deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)` }}>
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 lg:flex-row">
        <div className="text-center lg:sticky lg:top-24 lg:w-[36%] lg:shrink-0 lg:self-start lg:text-left">
          <h2 className="text-3xl font-light sm:text-4xl">FAQ</h2>
          <p className="mt-4 text-sm leading-relaxed text-white/65">
            Ainda precisa de ajuda?
          </p>
          <a
            href={CERT_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex rounded-full border border-white/20 px-6 py-3 text-xs font-bold tracking-wide transition hover:bg-white/10"
          >
            CONTATE-NOS
          </a>
          <img src={FAQ_IMG} alt="" aria-hidden="true" loading="lazy" decoding="async" className="mx-auto mt-8 h-auto w-full max-w-sm rounded-2xl lg:mx-0" />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          {FAQ.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold"
                  aria-expanded={isOpen}
                >
                  <span className="min-w-0">{f.q}</span>
                  {isOpen ? (
                    <Minus className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
                  )}
                </button>
                {isOpen && <p className="px-5 pb-5 text-sm leading-relaxed text-white/70">{f.a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
